import React, { useState } from 'react';

export default function Login({ backendUrl, onLogin, language }) {
  const [isSignup, setIsSignup] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    organization: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const t = (key) => {
    const translations = {
      en: {
        welcome: 'Welcome to',
        appTitle: 'SIH Sports Assessment',
        appSubtitle: 'AI-powered sports performance analysis',
        login: 'Login',
        signup: 'Sign Up',
        name: 'Full Name',
        email: 'Email',
        password: 'Password',
        organization: 'Organization (Optional)',
        noAccount: "Don't have an account?",
        haveAccount: 'Already have an account?',
        signupHere: 'Sign up here',
        loginHere: 'Login here',
        features: ['Real-time pose detection', 'Offline support', 'Secure & private', 'Leaderboards & rankings']
      },
      hi: {
        welcome: 'स्वागत है',
        appTitle: 'SIH खेल मूल्यांकन',
        appSubtitle: 'AI-संचालित खेल प्रदर्शन विश्लेषण',
        login: 'लॉगिन',
        signup: 'साइन अप',
        name: 'पूरा नाम',
        email: 'ईमेल',
        password: 'पासवर्ड',
        organization: 'संगठन (वैकल्पिक)',
        noAccount: 'खाता नहीं है?',
        haveAccount: 'पहले से खाता है?',
        signupHere: 'यहां साइन अप करें',
        loginHere: 'यहां लॉगिन करें',
        features: ['रियल-टाइम पोज़ डिटेक्शन', 'ऑफ़लाइन समर्थन', 'सुरक्षित और निजी', 'लीडरबोर्ड और रैंकिंग']
      }
    };
    return translations[language][key] || key;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const endpoint = isSignup ? '/api/auth/signup' : '/api/auth/login';
      const payload = isSignup 
        ? formData 
        : { email: formData.email, password: formData.password };

      const res = await fetch(`${backendUrl}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (res.ok) {
        onLogin(data.token, data.user);
      } else {
        setError(data.error || 'Authentication failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Auth error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white dark:bg-gray-900">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center text-white font-bold text-2xl shadow-lg mx-auto mb-4">
              AI
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {t('welcome')}
            </h1>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
              {t('appTitle')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">{t('appSubtitle')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isSignup && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('name')}
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                  placeholder="John Doe"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('email')}
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('password')}
              </label>
              <input
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                placeholder="••••••••"
                minLength="6"
              />
            </div>

            {isSignup && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('organization')}
                </label>
                <input
                  type="text"
                  value={formData.organization}
                  onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                  placeholder="Your school/club"
                />
              </div>
            )}

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-indigo-700 transition shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Please wait...' : (isSignup ? t('signup') : t('login'))}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {isSignup ? t('haveAccount') : t('noAccount')}{' '}
              <button
                onClick={() => {
                  setIsSignup(!isSignup);
                  setError('');
                }}
                className="text-blue-600 dark:text-blue-400 font-medium hover:underline"
              >
                {isSignup ? t('loginHere') : t('signupHere')}
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Features */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-blue-500 via-indigo-600 to-purple-600 p-12 items-center justify-center">
        <div className="max-w-md text-white">
          <h3 className="text-4xl font-bold mb-6">
            Transform Your Sports Performance
          </h3>
          <p className="text-blue-100 mb-8 text-lg">
            Advanced AI-powered analysis for accurate, real-time sports performance assessment.
          </p>
          
          <div className="space-y-4">
            {t('features').map((feature, i) => (
              <div key={i} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-xl">
                  {i === 0 && '🎯'}
                  {i === 1 && '📱'}
                  {i === 2 && '🔒'}
                  {i === 3 && '🏆'}
                </div>
                <span className="text-lg">{feature}</span>
              </div>
            ))}
          </div>

          <div className="mt-8 p-6 bg-white/10 backdrop-blur-sm rounded-xl">
            <p className="text-sm text-blue-100">
              "This platform has revolutionized how we assess athletic talent. The AI accuracy is incredible!"
            </p>
            <p className="text-white font-semibold mt-2">- Sports Coach, Delhi</p>
          </div>
        </div>
      </div>
    </div>
  );
}